import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Calendar, Users, Phone, Mail, AlertCircle, CheckCircle, MessageSquare, ThumbsUp, Clock, ArrowLeft, TrendingUp, Activity, Building2, ExternalLink, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import StatCard from '../components/ui/StatCard';
import StatusBadge from '../components/ui/StatusBadge';
import { useAuth } from '../components/AuthProvider';
import { supabase } from '../lib/auth';
import { formatDate, formatDateTime } from '../lib/utils';
import { getAppointmentsByProspectingDate } from '../lib/api';

interface ProspectingDay {
  id: string;
  date: string;
  client_id: string;
  sdr_id: string | null;
  sdr_first_name: string | null;
  sdr_last_name: string | null;
  calls_count: number;
  conversations_count: number;
  emails_sent: number;
  leads_lost: number;
  rdvs_booked: number;
  email: string | null;
  gpt_summary: string | null;
  leads_quality_feedback: string | null;
  technical_blockers: string | null;
  improvement_suggestions: string | null;
  sdr_feeling: string | null;
  created_at: string;
  clients?: {
    id: string;
    name: string;
  };
}

interface Appointment {
  id: string;
  title: string;
  status: string;
  date: string;
  time: string;
  prospect_name: string;
  job_title: string | null;
  company_name: string;
  booking_date: string;
  email_sales_hook: string | null;
  created_at: string;
}

const ProspectingDayDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { profile, isAuthenticated } = useAuth();
  const [prospectingDay, setProspectingDay] = useState<ProspectingDay | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backUrl, setBackUrl] = useState('/prospecting');
  const [backLabel, setBackLabel] = useState('Retour à la prospection');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fromClient = searchParams.get('from');

    if (fromClient) {
      setBackUrl(`/clients/${fromClient}`);
      setBackLabel('Retour au client');
    } else if (location.state?.from) {
      const fromPath = location.state.from;
      if (fromPath.startsWith('/clients/')) {
        setBackUrl(fromPath);
        setBackLabel('Retour au client');
      }
    }
  }, [location]);

  useEffect(() => {
    const fetchProspectingDay = async () => {
      if (!id || !profile || !isAuthenticated) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const { data, error } = await supabase
          .from('prospecting_days')
          .select(`
            *,
            clients (
              id,
              name
            )
          `)
          .eq('id', id)
          .maybeSingle();

        if (error) throw error;

        if (!data) {
          setError('Session de prospection introuvable');
          setIsLoading(false);
          return;
        }

        setProspectingDay(data);

        if (data) {
          setIsLoadingAppointments(true);
          try {
            const appointmentsData = await getAppointmentsByProspectingDate(data.client_id, data.date, data.email);
            setAppointments(appointmentsData);
          } catch (err) {
            console.error('Error fetching appointments:', err);
          } finally {
            setIsLoadingAppointments(false);
          }
        }
      } catch (error) {
        console.error('Error fetching prospecting day:', error);
        setError('Erreur lors du chargement de la session de prospection');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProspectingDay();
  }, [id, profile, isAuthenticated]);

  const getConversionRate = () => {
    if (!prospectingDay || !prospectingDay.calls_count || prospectingDay.calls_count === 0) return 0;
    return ((prospectingDay.rdvs_booked || 0) / prospectingDay.calls_count * 100);
  };

  const getStatusBadge = (status: string) => {
    return <StatusBadge status={status} size="sm" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 h-32">
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!prospectingDay) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center">
              <AlertCircle className="h-12 w-12 text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {error ? 'Erreur de chargement' : 'Session de prospection introuvable'}
            </h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              {error || 'La session de prospection que vous recherchez n\'existe pas ou a été supprimée.'}
            </p>
            <Link to={backUrl}>
              <Button variant="outline" icon={<ArrowLeft className="h-4 w-4" />}>
                {backLabel}
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center space-x-4 mb-6">
            <Link
              to={backUrl}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </Link>
            <div className="flex-1">
              <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-green-600 bg-clip-text text-transparent">
                Session du {formatDate(prospectingDay.date)}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <p className="text-lg text-gray-600">
                  Client : {prospectingDay.clients?.name}
                </p>
                {(prospectingDay.sdr_first_name || prospectingDay.sdr_last_name) && (
                  <div className="flex items-center text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    <Users className="h-4 w-4 mr-2" />
                    {prospectingDay.sdr_first_name} {prospectingDay.sdr_last_name}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6 mb-8"
        >
          <StatCard
            title="RDVs bookés"
            value={prospectingDay.rdvs_booked}
            icon={<CheckCircle className="h-5 w-5 text-green-600" />}
            className="bg-gradient-to-br from-green-50 to-emerald-100 border-green-200 sm:col-span-2 lg:col-span-1"
          />
          <StatCard
            title="Appels passés"
            value={prospectingDay.calls_count}
            icon={<Phone className="h-5 w-5 text-blue-600" />}
            className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200"
          />
          <StatCard
            title="Conversations"
            value={prospectingDay.conversations_count}
            icon={<MessageSquare className="h-5 w-5 text-purple-600" />}
            className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200"
          />
          <StatCard
            title="Emails envoyés"
            value={prospectingDay.emails_sent}
            icon={<Mail className="h-5 w-5 text-indigo-600" />}
            className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200"
          />
          <StatCard
            title="Taux conversion"
            value={`${getConversionRate().toFixed(1)}%`}
            icon={<TrendingUp className="h-5 w-5 text-pink-600" />}
            className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Card title="Métriques de performance" className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex items-center justify-between p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-900">Taux de conversation</p>
                    <p className="text-xs text-blue-700">Conversations / Appels</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-600">
                    {prospectingDay.calls_count > 0
                      ? ((prospectingDay.conversations_count / prospectingDay.calls_count) * 100).toFixed(1)
                      : '0.0'
                    }%
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-900">Efficacité RDV</p>
                    <p className="text-xs text-green-700">RDVs / Conversations</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">
                    {prospectingDay.conversations_count > 0
                      ? ((prospectingDay.rdvs_booked / prospectingDay.conversations_count) * 100).toFixed(1)
                      : '0.0'
                    }%
                  </p>
                </div>
              </div>

              {prospectingDay.leads_lost > 0 && (
                <div className="flex items-center justify-between p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-xl border border-red-200">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                      <AlertCircle className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-red-900">Leads perdus</p>
                      <p className="text-xs text-red-700">Opportunités manquées</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-red-600">{prospectingDay.leads_lost}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </motion.div>

        {prospectingDay.email && prospectingDay.rdvs_booked > 0 && appointments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-8"
          >
            <Card
              title={
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-green-600" />
                    <span>Rendez-vous pris ce jour</span>
                    <span className="text-sm font-normal text-gray-500">
                      ({appointments.length})
                    </span>
                  </div>
                  <Link
                    to="/appointments"
                    className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center space-x-1 transition-colors"
                  >
                    <span>Voir tous les rendez-vous</span>
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              }
              className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg"
            >
            {isLoadingAppointments ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {appointments.map((appointment, index) => (
                  <motion.div
                    key={appointment.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={`/appointments/${appointment.id}`}
                      className="block bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 rounded-xl p-4 border border-green-200 hover:border-green-300 transition-all duration-200 group"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors truncate mb-2">
                            {appointment.title}
                          </h4>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-2 text-green-600 flex-shrink-0" />
                              <span>{formatDateTime(appointment.date, appointment.time)}</span>
                            </div>

                            {appointment.job_title && (
                              <div className="flex items-center">
                                <Briefcase className="h-4 w-4 mr-2 text-green-600 flex-shrink-0" />
                                <span>{appointment.job_title}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <ExternalLink className="h-5 w-5 text-green-600" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </Card>
        </motion.div>
        )}

        {prospectingDay.gpt_summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <Card title="Résumé de la journée" className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
              <div className="space-y-4">
                {prospectingDay.gpt_summary.split('\n').map((paragraph, index) => {
                  const formattedText = paragraph.split(/(\*\*.*?\*\*)/).map((part, i) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      const text = part.slice(2, -2);
                      return <span key={i} className="text-green-600 font-semibold">{text}</span>;
                    }
                    return part;
                  });

                  return (
                    <p key={index} className="text-gray-700 leading-relaxed">
                      {formattedText}
                    </p>
                  );
                })}
              </div>
            </Card>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200 p-6 shadow-lg"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center text-sm text-gray-500">
              <Clock className="h-4 w-4 mr-2" />
              Session créée le {formatDate(prospectingDay.created_at)}
            </div>
            {profile?.role === 'super_admin' && (
              <div className="flex space-x-3">
                <Button variant="outline" size="sm" className="hover:bg-green-50 hover:border-green-300">
                  Modifier
                </Button>
                <Button size="sm" icon={<Activity className="h-4 w-4" />} className="bg-green-600 hover:bg-green-700">
                  Nouvelle session
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProspectingDayDetailPage;
